import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ContainerScroll,
  CardsContainer,
  CardTransformed,
} from './ui/animated-cards-stack';

const EDUCATION_CARDS = [
  {
    id: "cmu",
    title: "Carnegie Mellon University",
    description: ["M.S. Software Engineering", "Silicon Valley \u00b7 2026\u20132027"],
    image: "/photo/cmu.webp",
    gradient: "linear-gradient(135deg, rgb(37 99 235), rgb(30 64 175))",
  },
  {
    id: "cmu-memory",
    title: "CMU Memories",
    description: ["CMU 125 \u2014 The Power of Possibilities"],
    image: "/photo/cmu_memory.webp",
    gradient: "linear-gradient(135deg, rgb(249 115 22), rgb(194 65 12))",
  },
  {
    id: "psu",
    title: "Penn State University",
    description: ["B.S. Computer Science", "Minor in Mathematics \u00b7 2022\u20132025"],
    image: "/photo/psu_graduate.webp",
    gradient: "linear-gradient(135deg, rgb(147 51 234), rgb(107 33 168))",
  },
  {
    id: "cssa-gala",
    title: "CSSA Gala",
    description: ["Mid-Autumn Gala \u00b7 600+ attendees"],
    image: "/photo/gala.webp",
    gradient: "linear-gradient(135deg, rgb(34 197 94), rgb(21 128 61))",
  },
  {
    id: "game-day",
    title: "Game Day",
    description: ["Penn State football nights with the crowd"],
    image: "/photo/gameday.webp",
    gradient: "linear-gradient(135deg, rgb(168 85 247), rgb(126 34 206))",
  },
  {
    id: "journey",
    title: "Journey to PSU",
    description: ["The path that led me to University Park"],
    image: "/photo/DrivetoPSU.webp",
    gradient: "linear-gradient(135deg, rgb(99 102 241), rgb(67 56 202))",
  }
];

const EducationDetails: React.FC = () => {
  // lg 以下是上下布局，卡片飞不出屏幕，改为淡出
  const [isNarrow, setIsNarrow] = useState(false);
  // 手机上按"实际可用高度"算卡片高度：文字底下剩多少就给多少（190–280），保证一屏放得下
  const [phoneCardH, setPhoneCardH] = useState<number | null>(null);
  // 真正放不下的小屏（可用高度 < 600，如 iPhone SE）：退化成普通滚动 + 静态卡片
  const [isShort, setIsShort] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  // 卡片被压到 250px 以下时，文字区用更紧凑的排版
  const compactCard = phoneCardH !== null && phoneCardH < 250;

  useEffect(() => {
    const narrow = window.matchMedia('(max-width: 1023px)');
    const phone = window.matchMedia('(max-width: 639px)');

    // iOS Safari 的 innerHeight 会随工具栏伸缩变化；用 100svh（工具栏展开时的高度）做预算，
    // 布局按最小可视高度来排，工具栏收起时只是底下多点空间，不会来回抖
    const smallViewportHeight = () => {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none';
      document.body.appendChild(probe);
      const h = probe.getBoundingClientRect().height;
      probe.remove();
      return h || window.innerHeight;
    };

    const measure = () => {
      setIsNarrow(narrow.matches);
      if (!phone.matches) {
        setPhoneCardH(null);
        setIsShort(false);
        return;
      }
      const svh = smallViewportHeight();
      setIsShort(svh < 600);
      const sticky = stickyRef.current;
      const text = textRef.current;
      if (!sticky || !text) return;
      // 文字底部相对 sticky 容器顶部的位置（含顶部留白）
      const textBottom = text.getBoundingClientRect().bottom - sticky.getBoundingClientRect().top;
      const stackOffsets = 6 * 5; // 卡片错位叠加的额外高度
      const avail = svh - textBottom - stackOffsets - 16;
      setPhoneCardH(Math.round(Math.max(190, Math.min(280, avail))));
    };

    measure();
    // 字体加载完文字高度可能变，再量一次
    const t = window.setTimeout(measure, 800);
    window.addEventListener('resize', measure);
    narrow.addEventListener('change', measure);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', measure);
      narrow.removeEventListener('change', measure);
    };
  }, []);

  return (
    <section id="education" className="scroll-mt-16" style={{ backgroundColor: '#DCD2C6' }}>
      {/* Combined Section - Left-Right Layout */}
      <ContainerScroll className={isShort ? 'relative min-h-0' : 'h-[400vh] relative'}>
        <div
          ref={stickyRef}
          className={
            isShort
              ? 'relative z-0 flex justify-center pt-20 pb-10'
              : 'sticky top-0 z-0 min-h-screen flex items-start lg:items-center justify-center pt-20 pb-8'
          }
        >
          <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8 items-center">
              
              {/* Left Side - Text Section（手机上文字在上，卡片飞出时从文字面板底下穿过） */}
              <div ref={textRef} className="text-left lg:pr-8 relative z-10 bg-[#DCD2C6] lg:bg-transparent pb-5 -mb-5 lg:pb-0 lg:mb-0">
                <motion.h2
                  className="text-[#2C2721] text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                  style={{ fontFamily: 'Vixa-Regular, sans-serif' }}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  Education
                </motion.h2>
                <motion.div
                  className="text-[#2C2721] text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-black space-y-4"
                  style={{
                    fontFamily: 'Cardival-Demo, sans-serif',
                    fontVariantLigatures: 'none',
                    fontFeatureSettings: '"liga" 0, "clig" 0',
                    lineHeight: '1.4'
                  }}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <p>
                    Currently pursuing an M.S. in Software Engineering at{" "}
                    <motion.span
                      className="relative inline"
                      initial={{ borderBottomColor: 'transparent', boxShadow: '0 4px 0 -2px transparent' }}
                      whileInView={{
                        borderBottomColor: '#87CEFA',
                        boxShadow: '0 4px 0 -2px rgba(135, 206, 250, 0.6)'
                      }}
                      style={{ borderBottom: '2px solid transparent' }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      viewport={{ once: true, amount: 0.8 }}
                    >
                      Carnegie Mellon University
                    </motion.span>
                    , with a focus on building scalable software systems and AI-native products.
                  </p>
                  <p>
                    Previously, I graduated Magna Cum Laude from Penn State University with a B.S. in Computer Science, a Minor in Mathematics, and a{" "}
                    <motion.span
                      className="relative inline-block px-1 rounded"
                      style={{ color: '#2C2721' }}
                      initial={{ backgroundColor: 'transparent' }}
                      whileInView={{ backgroundColor: '#FFFF00' }}
                      transition={{ duration: 0.8, delay: 1.2 }}
                      viewport={{ once: true, amount: 0.8 }}
                    >
                      3.97/4.0
                    </motion.span>{" "}
                    GPA.
                  </p>
                  <p>
                    My academic path has taken me from algorithms and systems to machine learning, computer vision, and product engineering <span style={{ fontFamily: 'Georgia, serif' }}>—</span> shaping how I approach software today: build things that are technically solid, visually thoughtful, and genuinely useful.
                  </p>
                </motion.div>
              </div>

              {/* Right Side - Animated Cards */}
              <div className="flex justify-center lg:justify-end">
                <CardsContainer
                  className="relative h-[280px] sm:h-[400px] md:h-[480px] w-full max-w-[260px] sm:max-w-[400px] md:max-w-[480px] lg:max-w-[560px] mt-0 mb-12 lg:my-0"
                  style={phoneCardH ? {
                    height: phoneCardH,
                    maxWidth: Math.round(phoneCardH < 250 ? Math.min(260, phoneCardH * 1.2) : phoneCardH * 0.93),
                  } : undefined}
                >
                  {EDUCATION_CARDS.map((card, index) => (
                    <CardTransformed
                      arrayLength={EDUCATION_CARDS.length}
                      key={card.id}
                      index={index + 2}
                      incrementY={isNarrow ? 6 : 10}
                      variant="dark"
                      fadeOut={isNarrow}
                      animate={!isShort}
                      className="overflow-hidden !p-0 border-2 border-black !bg-black"
                      style={{ borderRadius: '0px' }}
                    >
                      <div className="relative h-full w-full flex flex-col">
                        {/* Image Section - Increased height */}
                        <div className={`w-full overflow-hidden ${compactCard ? 'h-[55%]' : 'h-[70%]'}`}>
                          <img
                            src={card.image}
                            alt={card.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        
                        {/* Content Section - Reduced height with black background */}
                        <div className={`flex-1 bg-black flex flex-col justify-center ${compactCard ? 'p-2' : 'p-3'}`}>
                          <h3 
                            className={`font-bold text-[#F1EFE0] text-center ${compactCard ? 'text-base leading-tight mb-0.5' : 'text-lg mb-1'}`}
                            style={{ fontFamily: 'Cardival-Demo, sans-serif' }}
                          >
                            {card.title}
                          </h3>
                          {card.description.map((line) => (
                            <p key={line} className={`text-[#F1EFE0]/80 text-center ${compactCard ? 'text-[11px] leading-tight' : 'text-xs leading-relaxed'}`} style={{ fontFamily: 'Homemade Apple, cursive' }}>
                              {line}
                            </p>
                          ))}
                        </div>
                      </div>
                    </CardTransformed>
                  ))}
                </CardsContainer>
              </div>
            </div>
          </div>
        </div>
      </ContainerScroll>
    </section>
  );
};

export default EducationDetails;
